import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import type { UploadLinksFilterInput } from '@frameio/graphql-client/generated';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { consumeEvent } from '@frameio/utils';
import { Text, theme } from '@frameio/vapor';
import type { IconProps } from '@frameio/vapor-icons';
import { IconCloudArrow } from '@frameio/vapor-icons';
import { useIsActiveItem, useOnItemSelect } from '../contexts';
import { useGetUploadLinksCountForProjectQuery } from '../gql/generated/GetUploadLinksCountForProject';
import type { ProjectTreeItem, UploadLinksTreeItemProps } from '../types';
import TEXT from './generated/TEXT/ProjectTreeUploadLinks';
import {
  BaseContainer,
  HiddenLink,
  ICON_WIDTH,
  ItemContainer,
} from './TreeItem';

/** The nav badge counts only active (non-expired) upload links. */
const ACTIVE_FILTER: UploadLinksFilterInput = { expired: false };

export const UploadLinks = ({
  id,
  isOpen,
  transition,
  type,
}: UploadLinksTreeItemProps) => {
  const item = useMemo<ProjectTreeItem>(() => ({ id, type }), [id, type]);

  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();
  const isActive = useIsActiveItem(item);
  const onItemSelect = useOnItemSelect();

  const { data } = useGetUploadLinksCountForProjectQuery({
    variables: {
      accountId: accountId ?? '',
      projectId: projectId ?? '',
      page: { first: 1 },
      filter: ACTIVE_FILTER,
    },
    skip: !accountId || !projectId,
  });
  const count = data?.account?.project?.uploadLinks?.totalCount ?? 0;
  const label = count > 0 ? TEXT.buttonLabelWithCount(count) : TEXT.buttonLabel;

  const href = useMemo(
    () =>
      getLink({
        page: 'uploadLinks',
        projectId,
      }),
    [projectId]
  );

  const isDisabled = transition === 'close';

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        onItemSelect(item, href);
        consumeEvent(event);
      }
    },
    [href, item, onItemSelect]
  );

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button === 2 || event.metaKey) return;

      onItemSelect(item, href);
    },
    [href, item, onItemSelect]
  );

  return (
    <BaseContainer
      key={id}
      $isActive={isActive}
      onClick={handleClick}
      onContextMenu={consumeEvent}
      onKeyDown={handleKeyDown}
    >
      <ItemContainer
        key={id}
        expandable={false}
        id={id}
        isActive={isActive}
        isDisabled={isDisabled}
        isLink={true}
        isOpen={isOpen}
        name={TEXT.buttonLabel}
        paddingLeft={PADDING_LEFT}
        type={type}
      >
        <IconCloudArrow {...ICON_PROPS} />

        <Text
          color={isActive ? 'primary' : undefined}
          truncate={true}
          variant="body"
        >
          {label}
        </Text>

        {href && (
          <HiddenLink aria-label={TEXT.buttonLabel} href={href} tabIndex={-1} />
        )}
      </ItemContainer>
    </BaseContainer>
  );
};

const PADDING_LEFT = 16;

const ICON_PROPS: IconProps = {
  color: theme.colors.icon.primary,
  size: ICON_WIDTH,
  style: { flexShrink: 0 },
};

TEXT.DEFINE({
  /** Label for the Upload Links link in the ProjectTree */
  buttonLabel: 'Upload Links',
  /** Label for the Upload Links link with a count of links in the project */
  buttonLabelWithCount: (count: number) => `Upload Links (${count})`,
});
